import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCampaign, getRecipients, addRecipientsBulk, getUploadUrl, uploadXlsxFile, runCampaign } from '../services/api';
import { format } from 'date-fns';

function CampaignDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddRecipients, setShowAddRecipients] = useState(false);
  const [newRecipients, setNewRecipients] = useState('');
  const [xlsxFile, setXlsxFile] = useState(null);
  const [runningCampaign, setRunningCampaign] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const pollingRef = useRef(null);

  useEffect(() => {
    fetchCampaignData();

    // Cleanup polling on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [id]);

  // Start polling when campaign is running
  useEffect(() => {
    if (campaign && ['importing', 'sending'].includes(campaign.status)) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [campaign?.status]);

  const fetchCampaignData = async () => {
    try {
      setLoading(true);
      const [campaignData, recipientsData] = await Promise.all([
        getCampaign(id, true),
        getRecipients(id)
      ]);
      setCampaign(campaignData);
      setRecipients(recipientsData.recipients || []);
      setError(null);
    } catch (err) {
      setError('Failed to load campaign details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecipients = async () => {
    try {
      const recipientList = parseRecipients(newRecipients);
      if (recipientList.length === 0) {
        alert('Please enter at least one recipient');
        return;
      }

      await addRecipientsBulk(id, recipientList);
      setNewRecipients('');
      setShowAddRecipients(false);
      fetchCampaignData();
    } catch (err) {
      setError('Failed to add recipients');
      console.error(err);
    }
  };

  const parseRecipients = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const recipientList = [];

    for (const line of lines) {
      const parts = line.split(',').map(p => p.trim());
      if (parts[0]) {
        recipientList.push({
          email: parts[0],
          name: parts[1] || ''
        });
      }
    }

    return recipientList;
  };

  const startPolling = () => {
    if (pollingRef.current) return; // Already polling

    console.log('Starting campaign progress polling...');
    pollingRef.current = setInterval(async () => {
      try {
        const campaignData = await getCampaign(id, true);
        setCampaign(campaignData);

        // Stop polling if campaign is complete
        if (['completed', 'failed'].includes(campaignData.status)) {
          stopPolling();
          fetchCampaignData(); // Fetch full data including recipients
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000); // Poll every 3 seconds
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      console.log('Stopping campaign progress polling');
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx')) {
        setError('Please select an .xlsx file');
        return;
      }
      setXlsxFile(file);
      setError(null);
    }
  };

  const handleRunCampaign = async () => {
    if (!xlsxFile) {
      setError('Please select an XLSX file first');
      return;
    }

    try {
      setRunningCampaign(true);
      setUploadProgress('Getting upload URL...');
      setError(null);

      // Get presigned URL
      const uploadData = await getUploadUrl(id);
      console.log('Got upload URL:', uploadData.s3_key);

      // Upload file to S3
      setUploadProgress('Uploading file to S3...');
      await uploadXlsxFile(uploadData.upload_url, xlsxFile);
      console.log('File uploaded successfully');

      // Trigger campaign run
      setUploadProgress('Starting campaign...');
      await runCampaign(id, uploadData.s3_key);
      console.log('Campaign started');

      // Reset file input
      setXlsxFile(null);
      setUploadProgress('Campaign running! Progress will update automatically.');

      // Start polling for progress
      fetchCampaignData();

    } catch (err) {
      console.error('Error running campaign:', err);
      setError(err.response?.data?.error || 'Failed to run campaign');
      setUploadProgress(null);
    } finally {
      setRunningCampaign(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading campaign details...</div>;
  }

  if (!campaign) {
    return <div className="error">Campaign not found</div>;
  }

  const stats = campaign.stats || {};
  const openRate = stats.open_rate || 0;

  return (
    <div className="campaign-details">
      <button
        className="button button-secondary"
        onClick={() => navigate('/campaigns')}
        style={{ marginBottom: '1rem' }}
      >
        ← Back to Campaigns
      </button>

      <h1>{campaign.name}</h1>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Recipients</h3>
          <div className="value">{stats.total_recipients || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Sent</h3>
          <div className="value">{stats.sent_count || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Opens</h3>
          <div className="value">{stats.open_count || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Open Rate</h3>
          <div className="value">{openRate}%</div>
        </div>
      </div>

      <div className="card">
        <h2>Campaign Details</h2>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <strong>Status:</strong>{' '}
            <span className={`status-badge status-${campaign.status}`}>
              {campaign.status || 'draft'}
            </span>
          </div>
          <div>
            <strong>From:</strong> {campaign.from_email}
          </div>
          <div>
            <strong>Subject:</strong> {campaign.subject}
          </div>
          <div>
            <strong>Created:</strong>{' '}
            {format(new Date(campaign.created_at * 1000), 'MMM dd, yyyy HH:mm')}
          </div>
          {campaign.total_recipients > 0 && (
            <>
              <div>
                <strong>Total Recipients:</strong> {campaign.total_recipients || 0}
              </div>
              {campaign.sent_count !== undefined && (
                <div>
                  <strong>Sent:</strong> {campaign.sent_count || 0} / {campaign.total_recipients || 0}
                  {campaign.total_recipients > 0 && ` (${Math.round((campaign.sent_count / campaign.total_recipients) * 100)}%)`}
                </div>
              )}
              {campaign.failed_count > 0 && (
                <div>
                  <strong>Failed:</strong> {campaign.failed_count}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Run Campaign Section */}
      <div className="card">
        <h2>Run Campaign</h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Upload an XLSX file with recipient emails and click "Run Campaign" to import recipients and send emails in batches of 1000.
        </p>

        {uploadProgress && (
          <div style={{ padding: '1rem', background: '#e3f2fd', borderRadius: '4px', marginBottom: '1rem' }}>
            <strong>Progress:</strong> {uploadProgress}
          </div>
        )}

        {['importing', 'sending'].includes(campaign.status) && (
          <div style={{ padding: '1rem', background: '#fff3cd', borderRadius: '4px', marginBottom: '1rem' }}>
            <strong>Campaign is running...</strong>
            <div style={{ marginTop: '0.5rem' }}>
              Status: {campaign.status} |
              Sent: {campaign.sent_count || 0} / {campaign.total_recipients || 0}
              {campaign.total_recipients > 0 && (
                <div style={{ marginTop: '0.5rem', background: '#ddd', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${((campaign.sent_count || 0) / campaign.total_recipients) * 100}%`,
                    height: '100%',
                    background: '#4caf50',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
          <input
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
            disabled={runningCampaign || ['importing', 'sending'].includes(campaign.status)}
            style={{ flex: 1 }}
          />
          {xlsxFile && (
            <span style={{ color: '#4caf50' }}>✓ {xlsxFile.name}</span>
          )}
        </div>

        <button
          className="button button-primary"
          onClick={handleRunCampaign}
          disabled={!xlsxFile || runningCampaign || ['importing', 'sending'].includes(campaign.status)}
          style={{ width: '100%' }}
        >
          {runningCampaign ? 'Starting...' : ['importing', 'sending'].includes(campaign.status) ? 'Campaign Running...' : 'Run Campaign'}
        </button>

        {campaign.status === 'completed' && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#d4edda', borderRadius: '4px' }}>
            <strong>✓ Campaign completed!</strong> Sent {campaign.sent_count} emails.
          </div>
        )}

        {campaign.status === 'failed' && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8d7da', borderRadius: '4px' }}>
            <strong>✗ Campaign failed</strong> {campaign.error && `: ${campaign.error}`}
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Recipients ({recipients.length})</h2>
          <button
            className="button button-primary"
            onClick={() => setShowAddRecipients(!showAddRecipients)}
          >
            {showAddRecipients ? 'Cancel' : 'Add Recipients'}
          </button>
        </div>

        {showAddRecipients && (
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f5f5f5', borderRadius: '4px' }}>
            <textarea
              value={newRecipients}
              onChange={(e) => setNewRecipients(e.target.value)}
              placeholder="email@example.com,John Doe
another@example.com,Jane Smith"
              style={{ width: '100%', minHeight: '100px', marginBottom: '1rem' }}
            />
            <button className="button button-primary" onClick={handleAddRecipients}>
              Add Recipients
            </button>
          </div>
        )}

        {recipients.length === 0 ? (
          <p>No recipients added yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                  <th style={{ padding: '0.75rem' }}>Email</th>
                  <th style={{ padding: '0.75rem' }}>Name</th>
                  <th style={{ padding: '0.75rem' }}>Status</th>
                  <th style={{ padding: '0.75rem' }}>Sent At</th>
                  <th style={{ padding: '0.75rem' }}>Opened At</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map(recipient => (
                  <tr key={recipient.recipient_id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.75rem' }}>{recipient.email}</td>
                    <td style={{ padding: '0.75rem' }}>{recipient.name || '-'}</td>
                    <td style={{ padding: '0.75rem' }}>
                      <span className={`status-badge status-${recipient.status}`}>
                        {recipient.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {recipient.sent_at
                        ? format(new Date(recipient.sent_at * 1000), 'MMM dd, HH:mm')
                        : '-'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {recipient.opened_at
                        ? format(new Date(recipient.opened_at * 1000), 'MMM dd, HH:mm')
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Email Preview</h2>
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '1rem',
            background: 'white',
            maxHeight: '400px',
            overflow: 'auto'
          }}
          dangerouslySetInnerHTML={{ __html: campaign.html_body }}
        />
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}

export default CampaignDetails;
